import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import Dashboard from "./pages/Dashboard";
import TenantHome from "./pages/TenantHome";
import TenantInvoices from "./pages/TenantInvoices";
import TenantPayments from "./pages/TenantPayments";
import TenantDocuments from "./pages/TenantDocuments";
import TenantRequests from "./pages/TenantRequests";
import Properties from "./pages/Properties";
import Tenants from "./pages/Tenants";
import Payments from "./pages/Payments";
import Expenses from "./pages/Expenses";
import Invoices from "./pages/Invoices";
import Maintenance from "./pages/Maintenance";
import Vendors from "./pages/Vendors";
import Reports from "./pages/Reports";
import Documents from "./pages/Documents";
import Calendar from "./pages/Calendar";
import Messages from "./pages/Messages";
import AIAssistant from "./pages/AIAssistant";
import OutreachAgent from "./pages/OutreachAgent";
import Settings from "./pages/Settings";
import UserManagement from "./pages/UserManagement";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import UpdatePassword from "./pages/UpdatePassword";
import { useAuth } from "./contexts/AuthContext";
import { canAccessPath } from "./lib/permissions";
import { isSupabaseConfigured, supabaseConfigError } from "./lib/supabase";

const pageTitles = {
  "/": "Dashboard",
  "/tenant/invoices": "My Invoices",
  "/tenant/payments": "My Payments",
  "/tenant/documents": "My Documents",
  "/tenant/requests": "My Requests",
  "/properties": "Properties",
  "/tenants": "Tenants",
  "/payments": "Payments",
  "/expenses": "Expenses",
  "/invoices": "Invoices",
  "/maintenance": "Maintenance",
  "/vendors": "Vendors",
  "/messages": "Messages",
  "/documents": "Documents",
  "/calendar": "Calendar",
  "/reports": "Reports",
  "/ai-assistant": "AI Assistant",
  "/outreach-agent": "Outreach Agent",
  "/user-management": "User Management",
  "/settings": "Settings",
  "/login": "Login",
  "/update-password": "Update Password",
  "/privacy": "Privacy Policy",
  "/terms": "Terms of Use",
  "/contact": "Contact",
};

function ProtectedRoute({ children, path }) {
  const { user, profile, loading, signOut } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">
        Loading SpaceM8...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (profile?.status === "Suspended") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 text-white">
        <div className="max-w-md rounded-3xl border border-slate-800 bg-slate-900 p-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
            Access Suspended
          </p>
          <h1 className="mt-3 text-2xl font-semibold">This login is not currently active.</h1>
          <p className="mt-3 text-sm text-slate-300">
            Ask a Growth or Administrator user to reactivate your SpaceM8 access from Settings.
          </p>
          <button
            type="button"
            onClick={signOut}
            className="mt-6 h-12 rounded-2xl bg-orange-500 px-5 font-medium text-white hover:bg-orange-600"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  if (path && !canAccessPath(profile?.role, path)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 text-white">
        <div className="max-w-md rounded-3xl border border-slate-800 bg-slate-900 p-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
            Access Restricted
          </p>
          <h1 className="mt-3 text-2xl font-semibold">Your role cannot open this page.</h1>
          <p className="mt-3 text-sm text-slate-300">
            Ask a Growth or Administrator user to update your SpaceM8 role if you need access.
          </p>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="mt-6 h-12 rounded-2xl bg-orange-500 px-5 font-medium text-white hover:bg-orange-600"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return children;
}

function ConfigErrorScreen() {
  return (
    <main className="min-h-screen bg-slate-950 px-4 py-8 text-white">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl items-center justify-center">
        <section className="w-full rounded-[2rem] border border-slate-800 bg-slate-900 p-8 shadow-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
            Configuration Required
          </p>
          <h1 className="mt-3 text-3xl font-semibold">SpaceM8 cannot connect to Supabase yet.</h1>
          <p className="mt-4 text-sm leading-6 text-slate-300">
            Add the required environment variables to your project configuration, then restart the app.
          </p>
          <div className="mt-6 rounded-3xl border border-amber-800 bg-amber-950/60 p-4 text-sm text-amber-200">
            {supabaseConfigError}
          </div>
          <div className="mt-6 rounded-3xl border border-slate-800 bg-slate-950 p-4 text-sm text-slate-300">
            <p>`REACT_APP_SUPABASE_URL`</p>
            <p className="mt-2">`REACT_APP_SUPABASE_ANON_KEY`</p>
          </div>
        </section>
      </div>
    </main>
  );
}

function App() {
  const location = useLocation();
  const { profile } = useAuth();
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("spacem8_theme");
    return saved ? JSON.parse(saved) : false;
  });

  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem("spacem8_currency") || "KES";
  });

  useEffect(() => {
    localStorage.setItem("spacem8_theme", JSON.stringify(isDark));
  }, [isDark]);

  useEffect(() => {
    localStorage.setItem("spacem8_currency", currency);
  }, [currency]);

  useEffect(() => {
    const currentPageTitle = pageTitles[location.pathname];
    document.title = currentPageTitle ? `${currentPageTitle} | Space-m8` : "Space-m8";
  }, [location.pathname]);

  if (!isSupabaseConfigured) {
    return <ConfigErrorScreen />;
  }

  const isTenant = String(profile?.role || "").trim().toLowerCase() === "tenant";

  return (
    <Routes>
      <Route path="/login" element={<Login isDark={isDark} />} />
      <Route path="/update-password" element={<UpdatePassword isDark={isDark} />} />
      <Route
        path="/"
        element={
          <ProtectedRoute path="/">
            {isTenant ? (
              <TenantHome
                isDark={isDark}
                setIsDark={setIsDark}
                currency={currency}
                setCurrency={setCurrency}
              />
            ) : (
              <Dashboard
                isDark={isDark}
                setIsDark={setIsDark}
                currency={currency}
                setCurrency={setCurrency}
              />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties"
        element={
          <ProtectedRoute path="/properties">
            <Properties
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/invoices"
        element={
          <ProtectedRoute path="/tenant/invoices">
            <TenantInvoices
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/payments"
        element={
          <ProtectedRoute path="/tenant/payments">
            <TenantPayments
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/documents"
        element={
          <ProtectedRoute path="/tenant/documents">
            <TenantDocuments
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/requests"
        element={
          <ProtectedRoute path="/tenant/requests">
            <TenantRequests
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenants"
        element={
          <ProtectedRoute path="/tenants">
            <Tenants
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute path="/payments">
            <Payments
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute path="/invoices">
            <Invoices
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute path="/expenses">
            <Expenses
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute path="/maintenance">
            <Maintenance
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <ProtectedRoute path="/vendors">
            <Vendors
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute path="/reports">
            <Reports
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute path="/documents">
            <Documents
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute path="/calendar">
            <Calendar
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute path="/messages">
            <Messages
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <ProtectedRoute path="/ai-assistant">
            <AIAssistant
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outreach-agent"
        element={
          <ProtectedRoute path="/outreach-agent">
            <OutreachAgent
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute path="/user-management">
            <UserManagement
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute path="/settings">
            <Settings
              isDark={isDark}
              setIsDark={setIsDark}
              currency={currency}
              setCurrency={setCurrency}
            />
          </ProtectedRoute>
        }
      />
      <Route
  path="/privacy"
  element={
    <Privacy
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
    />
  }
/>

<Route
  path="/terms"
  element={
    <Terms
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
    />
  }
/>

<Route
  path="/contact"
  element={
    <Contact
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
    />
  }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
